import { config, format, createLogger, transports } from 'winston';

const {
  combine, timestamp, simple, json,
} = format;
const {
  LOG_LEVEL = 'info',
  NODE_ENV = 'production',
} = process.env;
const npmLogLevels = config.npm.levels; // BTW, we use npm log levels
const maximumStandardLevel = npmLogLevels[LOG_LEVEL];
let debugFilters = [];

const errorToJson = (error) => {
  const result = {};
  Object.getOwnPropertyNames(error).forEach((key) => {
    result[key] = error[key];
  });
  return result;
};

const formatFixes = (info) => {
  if (!info.meta) return info;

  if (info.meta instanceof Error) {
    return { ...info, meta: { error: errorToJson(info.meta) } };
  }

  const { meta } = info;
  Object.keys(info.meta).forEach((key) => {
    if (info.meta[key] instanceof Error) {
      meta[key] = errorToJson(info.meta[key]);
    }
  });
  return { ...info, meta };
};

const filterDebugs = format((info) => {
  // If standard levels, just pass through.
  if (npmLogLevels[info.level] <= maximumStandardLevel) return formatFixes(info);

  // If no filter defined, return false. This is to make sure performance is at a peak when nothing custom is needed.
  if (!debugFilters.length) return false;

  // If non-standard and filter is set, must be in filter list to permit.
  if (
    debugFilters.indexOf(`${info?.level}.${info?.fileOrClass}`) >= 0
    || debugFilters.indexOf(`${info?.level}.${info?.fileOrClass}.${info?.methodOrKey}`) >= 0
  ) {
    return formatFixes(info);
  }
  return false;
});

// Simple makes it way easier to read the logs for locals
const logFormat = NODE_ENV === 'production' ? json : simple;

const logService = createLogger({
  level: 'silly', // Must be set to maximum. Filter will take care of standard level.
  format: combine(
    filterDebugs(),
    timestamp(),
    logFormat(),
  ),
  transports: [
    new transports.Console({ colorize: true }),
  ],
});

const logger = {
  logService,
  addFilters: (filterList) => {
    debugFilters = debugFilters.concat(filterList);
  },
  removeFilters: (filterList) => {
    filterList.forEach((item) => {
      const index = debugFilters.indexOf(item);
      if (index !== -1) {
        debugFilters.splice(index, 1);
      }
    });
  },
  clearFilters: () => {
    debugFilters = [];
  },
  setFilters: (filterList) => {
    debugFilters = filterList;
  },
  getFilters: () => debugFilters,

  /**
   * Standard Debug Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  silly: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.silly({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  /**
   * Standard Debug Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  verbose: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.verbose({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  /**
   * Standard Debug Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  debug: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.debug({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  /**
   * Standard Info Log Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  info: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.info({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  /**
   * Standard Warn Log Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  warn: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.warn({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  /**
   * Standard Error Log Method
   * @param fileOrClass String
   * @param methodOrKey String
   * @param message String
   * @param meta Object
   */
  error: (fileOrClass = '', methodOrKey = '', message = '', meta = {}) => {
    logService.error({
      fileOrClass,
      methodOrKey,
      message,
      meta,
    });
  },

  createStandardLogger: (fileOrClass = '', methodOrKey = '') => ({
    debug: (message) => logger.debug(fileOrClass, methodOrKey, message),
    info: (message) => logger.info(fileOrClass, methodOrKey, message),
    warn: (message) => logger.warn(fileOrClass, methodOrKey, message),
    error: (message) => logger.error(fileOrClass, methodOrKey, message),
  }),
};

export { logger as default };
